import axios from 'axios';
import { Dimmer, Loader } from 'semantic-ui-react';
import { config } from '../../contexts/UrlContext';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { OpenIdProvider, OpenIdSettings } from '../Interfaces/AuthInterfaces';
import { decrypt } from '../../services/Encryption';
import WindowsAuth from './WindowsAuth';
import { AuthProvider } from "react-oidc-context";
import OpenId from './OpenId';

function AuthApp() {
    const [loading, setLoading] = useState<boolean>(true);
    const [useAuth, setUseAuth] = useState<boolean>(false);
    const [oidcConfig, setOidcConfig] = useState<any>({});
    const [openIdSettings, setOpenIdSettings] = useState<OpenIdSettings>({} as OpenIdSettings);
    const [openId, setOpenId] = useState<boolean>(false);

    const getAuthConfig = useCallback(() => {
        return axios.get(`${config.baseUrl}/api/Auth/GetAuthConfig`)
            .then(res => res.data)
            .catch((error) => {
                console.log(error);
                toast.error("An error occurred, please check logs");
            });
    }, []);

    const onSignIn = useCallback((redirectUri: string) => {
        window.history.pushState({}, "", redirectUri)
    }, []);

    const addAuth = useCallback(() => {
        getAuthConfig().then((res: OpenIdSettings) => {
            if (!res || res === null || !res || res.type === OpenIdProvider.None) {
                setLoading(false);
                return;
            } else {
                let oidcConfigTemp: any;
                let tempSettings: OpenIdSettings;

                if (res.type === OpenIdProvider.Microsoft) {
                    oidcConfigTemp = {
                        type: res.type,
                        authority: decrypt(res.authority),
                        client_id: decrypt(res.clientId),
                        redirect_uri: decrypt(res.redirectUri),
                        scope: `openid profile email api://${decrypt(res.clientId)}/grant.access`,
                        automaticSilentRenew: false, // DISABLE iframe-based silent renewal
                        includeIdTokenInSilentRenew: false, // DISABLE iframe inclusion
                        // Remove silentRequestTimeoutInSeconds completely
                        metadata: {
                            issuer: decrypt(res.issuer),
                            authorization_endpoint: decrypt(res.authorization_endpoint),
                            token_endpoint: decrypt(res.token_endpoint),
                            jwks_uri: decrypt(res.jwks_uri),
                            userinfo_endpoint: decrypt(res.userinfo_endpoint),
                        },
                    };

                    tempSettings = {
                        type: res.type,
                        authority: decrypt(res.authority),
                        clientId: decrypt(res.clientId),
                        redirectUri: decrypt(res.redirectUri),
                        issuer: decrypt(res.issuer),
                        authorization_endpoint: decrypt(res.authorization_endpoint),
                        token_endpoint: decrypt(res.token_endpoint),
                        jwks_uri: decrypt(res.jwks_uri),
                        userinfo_endpoint: decrypt(res.userinfo_endpoint),
                    }
                } else {
                    // For Okta, also disable iframe-based renewal
                    oidcConfigTemp = {
                        type: res.type,
                        authority: decrypt(res.authority) + "/oauth2/default",
                        client_id: decrypt(res.clientId),
                        redirect_uri: decrypt(res.redirectUri),
                        scope: "openid profile email",
                        automaticSilentRenew: false, // DISABLE iframe-based silent renewal
                        includeIdTokenInSilentRenew: false, // DISABLE iframe inclusion
                        // Remove silentRequestTimeoutInSeconds
                        // Remove silent_redirect_uri
                        loadUserInfo: true,
                        // Remove monitorSession and checkSessionInterval to avoid session monitoring

                        // Keep the custom storage implementation for quota handling
                        userStore: {
                            // Custom storage implementation to handle quota errors
                            set: (key: string, value: any) => {
                                try {
                                    localStorage.setItem(key, JSON.stringify(value));
                                } catch (e) {
                                    let error = e as DOMException;
                                    if (error.name === 'QuotaExceededError' || error.message?.includes('setItem')) {
                                        // Clean up and retry
                                        console.warn('Storage quota exceeded during userStore.set, cleaning up...');

                                        // Remove expired items
                                        const keysToCheck: any[] = [];
                                        for (let i = 0; i < localStorage.length; i++) {
                                            const storageKey = localStorage.key(i);
                                            if (storageKey && (storageKey.includes('oidc') || storageKey.includes('token'))) {
                                                keysToCheck.push(storageKey);
                                            }
                                        }

                                        keysToCheck.forEach(storageKey => {
                                            try {
                                                const item = localStorage.getItem(storageKey);
                                                if (item) {
                                                    const parsed = JSON.parse(item);
                                                    if (parsed.expires_at && parsed.expires_at < Date.now() / 1000) {
                                                        localStorage.removeItem(storageKey);
                                                    }
                                                }
                                            } catch (parseError) {
                                                // Ignore parse errors
                                            }
                                        });

                                        // Retry storage
                                        try {
                                            localStorage.setItem(key, JSON.stringify(value));
                                        } catch (retryError) {
                                            console.error('Failed to store after cleanup:', retryError);
                                            // Use sessionStorage as fallback
                                            try {
                                                sessionStorage.setItem(key, JSON.stringify(value));
                                            } catch (sessionError) {
                                                console.error('Failed to store in sessionStorage:', sessionError);
                                            }
                                        }
                                    }
                                }
                            },
                            get: (key: string) => {
                                try {
                                    const item = localStorage.getItem(key) || sessionStorage.getItem(key);
                                    return item ? JSON.parse(item) : null;
                                } catch (error) {
                                    console.error('Error retrieving from userStore:', error);
                                    return null;
                                }
                            },
                            remove: (key: string) => {
                                try {
                                    localStorage.removeItem(key);
                                    sessionStorage.removeItem(key);
                                } catch (error) {
                                    console.error('Error removing from userStore:', error);
                                }
                            },
                            getAllKeys: () => {
                                const keys = [];
                                for (let i = 0; i < localStorage.length; i++) {
                                    const key = localStorage.key(i);
                                    if (key) keys.push(key);
                                }
                                for (let i = 0; i < sessionStorage.length; i++) {
                                    const key = sessionStorage.key(i);
                                    if (key && !keys.includes(key)) keys.push(key);
                                }
                                return keys;
                            }
                        },
                    };

                    tempSettings = {
                        type: res.type,
                        authority: decrypt(res.authority) + "/oauth2/default",
                        clientId: decrypt(res.clientId),
                        redirectUri: decrypt(res.redirectUri)
                    }
                }
                setOpenIdSettings(tempSettings);
                setOidcConfig(oidcConfigTemp);
                setUseAuth(true);
                setOpenId(true);
                setLoading(false);
            }
        });
    }, [getAuthConfig]);

    useEffect(() => {
        addAuth();
    }, [addAuth]);

    // Memoize AuthProvider props to prevent re-renders
    const authProviderProps = useMemo(() => ({
        ...oidcConfig,
        onSigninCallback: () => { onSignIn(oidcConfig.redirect_uri) },
        onSigninSilentCallback: () => {
            console.log('Silent signin completed');
        },
        onSignoutCallback: () => {
            console.log('User signed out');
            window.location.href = '/';
        }
    }), [oidcConfig, onSignIn]);

    if (loading) {
        return (
            <Dimmer inverted active={true}>
                <Loader inverted > Loading CM2Webshare...</Loader>
            </Dimmer>
        );
    } else {
        if (useAuth) {
            return (
                <AuthProvider {...authProviderProps}>
                    <OpenId openIdSettings={openIdSettings} setOpenIdSettings={setOpenIdSettings} />
                </AuthProvider>
            );
        } else {
            return (
                <WindowsAuth openIdSettings={openIdSettings} setOpenIdSettings={setOpenIdSettings} />
            );
        }
    }
}

export default AuthApp;